import {
    AutocompleteInput,
    Breadcrumb,
    DateInput,
    Edit,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, DollarSign, User } from "lucide-react";
import { useDataProvider, useNotify, useRecordContext, useRedirect } from "ra-core";
import { useWatch } from "react-hook-form";
import { Link } from "react-router";
import { FileInput } from "@/components/admin/file-input";
import { FilteredContactInput } from "../misc/FilteredContactInput";
import { AttachmentField } from "../notes/AttachmentField";
import { ProductRequirements } from "./ProductRequirements";
import { ensureOpportunityForEnquiry } from "./ensureOpportunity";
import { StatusSelectInput } from "../documents/shared/StatusSelectInput";

const OPPORTUNITY_STAGES = ['in_discussion', 'qualified'];

const EnquiryEditBreadcrumb = () => {
    const record = useRecordContext();
    return (
        <Breadcrumb>
            <Breadcrumb.Item><Link to="/enquiries">Enquiries</Link></Breadcrumb.Item>
            {record && (
                <Breadcrumb.Item>
                    <Link to={`/enquiries/${record.id}/show`}>{record.reference}</Link>
                </Breadcrumb.Item>
            )}
            <Breadcrumb.PageItem>Edit</Breadcrumb.PageItem>
        </Breadcrumb>
    );
};

const LostReasonInput = () => {
    const status = useWatch({ name: 'status' });
    if (status !== 'disqualified') return null;

    return (
        <div className="mt-4 p-4 rounded-lg bg-red-50 border border-red-200">
            <TextInput
                source="lost_reason"
                label="Reason for Disqualification"
                multiline
                rows={2}
                className="w-full"
            />
        </div>
    );
};

export const EnquiryEdit = () => {
    const notify = useNotify();
    const redirect = useRedirect();
    const dataProvider = useDataProvider();

    const onSuccess = async (data: any, variables: any, _context: any) => {
        notify('Enquiry updated successfully', { type: 'success' });
        
        // Auto-create Opportunity when moving into 'in_discussion' or 'qualified'
        // (an Enquiry can reach 'qualified' directly, e.g. via Lead conversion,
        // without ever passing through 'in_discussion' — either transition
        // should still produce an Opportunity).
        if (
            OPPORTUNITY_STAGES.includes(data.status) &&
            !OPPORTUNITY_STAGES.includes(variables.previousData?.status)
        ) {
            try {
                const created = await ensureOpportunityForEnquiry(dataProvider, data);
                if (created) notify('Opportunity created automatically', { type: 'info' });
            } catch (error: any) {
                console.error(error);
                notify(`Error creating opportunity: ${error.message}`, { type: 'warning' });
            }
        }
        
        redirect('list', 'enquiries');
    };

    return (
        <Edit disableBreadcrumb title="Edit Enquiry" mutationMode="pessimistic" mutationOptions={{ onSuccess }}>
            <SimpleForm className="max-w-5xl mx-auto space-y-6">
                <EnquiryEditBreadcrumb />

                {/* Header Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <ClipboardList className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Enquiry Details</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <TextInput source="reference" label="Enquiry No." disabled className="w-full font-mono opacity-70" helperText="Auto-generated" />
                            <SelectInput
                                source="source"
                                label="Lead Source"
                                choices={[
                                    { id: 'website', name: 'Website' },
                                    { id: 'email', name: 'Email' },
                                    { id: 'phone', name: 'Phone / WhatsApp' },
                                    { id: 'referral', name: 'Referral' },
                                    { id: 'trade_show', name: 'Trade Show' },
                                    { id: 'cold_call', name: 'Cold Calling' },
                                ]}
                                className="w-full"
                            />
                            <StatusSelectInput
                                source="status"
                                label="Status"
                                choices={[
                                    { id: 'new', name: 'New' },
                                    { id: 'in_discussion', name: 'In Discussion' },
                                    { id: 'qualified', name: 'Qualified' },
                                    { id: 'disqualified', name: 'Disqualified' },
                                    { id: 'converted', name: 'Converted' },
                                    { id: 'closed', name: 'Closed' },
                                ]}
                                className="w-full"
                            />
                        </div>
                        <LostReasonInput />
                    </CardContent>
                </Card>

                {/* Customer Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Customer Information</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="assigned_to" reference="sales">
                                <AutocompleteInput label="Assigned Sales Rep" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            {/* V-8: Initial/Qualification Score + checklist removed per client request */}
                        </div>
                        <div className="pt-4">
                            <FileInput
                                source="attachments"
                                label="Attachments (drawings, PDF, images)"
                                multiple
                                accept={{
                                    "application/pdf": [".pdf"],
                                    "image/*": [".png", ".jpg", ".jpeg"],
                                    "application/msword": [".doc"],
                                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                                    "application/vnd.ms-excel": [".xls"],
                                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                                }}
                            >
                                <AttachmentField source="src" title="title" target="_blank" />
                            </FileInput>
                        </div>
                    </CardContent>
                </Card>

                {/* Requirements Card (E-3 / V-9: Budget removed per client request) */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Requirements</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <DateInput source="requirements.timeline" label="Expected Timeline" className="w-full" />
                            <ProductRequirements />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput label="Company" className="w-full" />
                            </ReferenceInput>
                            <FilteredContactInput label="Contact Person" className="w-full" />
                            <ReferenceInput source="ops_head_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Head" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                            <ReferenceInput source="ops_team_member_id" reference="sales">
                                <AutocompleteInput label="Assigned Ops Team Member" className="w-full" optionText={(record: any) => record ? `${record.first_name} ${record.last_name}` : ''} />
                            </ReferenceInput>
                        </div>
                    </CardContent>
                </Card>
            </SimpleForm>
        </Edit>
    );
};
